import React, {FC} from "react";
import {Popover, Tooltip} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import {DOCS_BASE_URL, HELP_MAP} from "../../../models/helpLinks";
import css from "./Reports.less";

/**
 * Erklärungen zu den Kacheln: was die Zahl zeigt, wie sie entsteht und was man daraus macht.
 * Dieselben Regeln stehen ausführlicher in der Anleitung (ko-docs, erste-schritte/auswertungen).
 * Wer hier eine Rechenregel ändert, zieht die Doku nach.
 */
// Ein Ort für den Slug: HELP_MAP ist durch check:help-links und den ko-docs-Vertrag abgesichert.
export const REPORTS_DOCS_SLUG = HELP_MAP["/dashboard/reports"];

export type TileId = "potential" | "heatmap" | "compare" | "sources" | "repeat" | "funnel";

export type TileHelp = {
    what: string;
    how: Array<string>;
    use: string;
};

export const tileHelp: Record<TileId, TileHelp> = {
    potential: {
        what: "Umsatz, der in laufenden und bald startenden Kursen noch frei ist: jeder freie Platz mal dem regulären Kurspreis.",
        how: [
            "Zählt Kurse, die gerade laufen oder in den nächsten 30 Tagen starten. Archivierte Kurse und Kurse mit Einzelbuchung bleiben außen vor.",
            "Freie Plätze = maximale Teilnehmerzahl minus aktive Buchungen und Platzreservierungen. Wartelisteneinträge zählen nicht als belegt.",
            "Preis = günstigster regulärer Kurspreis ohne Rabatte und Sonderpreise; bei Bezahlung je Kurseinheit mal die noch ausstehenden Einheiten.",
            "„je Monat“ verteilt den Betrag auf die Restlaufzeit ab heute, bei künftigen Kursen auf die ganze Laufzeit (mindestens ein Monat); „je Laufzeit“ zeigt ihn für den ganzen restlichen Kurs.",
            "Stand jetzt, nicht aus der nächtlichen Aufbereitung.",
        ],
        use: "Die Standorte und Kurse oben in der Liste bringen am meisten, wenn du sie bewirbst, Wartelisten anderer Kurse dorthin lenkst oder schwach belegte Kurse zusammenlegst.",
    },
    heatmap: {
        what: "Wie voll deine Kurseinheiten je Wochentag und Startstunde sind, zusammengefasst über den gewählten Zeitraum.",
        how: [
            "Je Feld: belegte Plätze geteilt durch alle Plätze der Kurseinheiten, die an diesem Wochentag in dieser Stunde beginnen.",
            "Belegt sind aktive Buchungen und Platzreservierungen der Einheit. Wartelisteneinträge und Stornos zählen nicht.",
            "Bei Einzelbuchung zählt jede gebuchte Einheit einzeln; bei Standardbuchungen zählen die gebuchten Einheiten, ein später Einstieg also erst ab dem Einstieg.",
            "Geplante Einheiten bis 60 Tage voraus sind enthalten, vergangene Einheiten archivierter Kurse ebenfalls.",
            "Vollste und freieste Stunde berücksichtigen nur Stunden mit mindestens zwei Einheiten, solange es genug davon gibt.",
        ],
        use: "Dunkle Felder sind ausgebucht: dort lohnt ein Zusatzkurs, wenn auch die Warteliste voll ist. Helle Felder haben Luft: Kurse zusammenlegen, verlegen oder gezielt bewerben.",
    },
    compare: {
        what: "Fünf Kennzahlen des Zeitraums gegenüber Vorperiode oder Vorjahr. Die Linie zeigt den Zahlungseingang je Monat über zwölf Monate, grau das Vorjahr.",
        how: [
            "Buchungen: neu angelegte Buchungen nach Buchungsdatum. Später stornierte bleiben mitgezählt; Wartelisteneinträge zählen erst, wenn sie nachrücken, und dann am Tag ihrer Anlage.",
            "Buchungswert: Summe der Buchungspreise dieser Buchungen (brutto, nach Rabatt), unabhängig davon, ob schon bezahlt wurde.",
            "Zahlungseingang: Raten, die im Zeitraum als bezahlt erfasst wurden, egal wann gebucht wurde.",
            "Stornos: Stornierungen nach Stornodatum, auch von Wartelisteneinträgen.",
            "Neue Kunden: im Zeitraum neu angelegte Bucher, online wie im Büro.",
            "Vorperiode = derselbe Abschnitt der vorigen Periode, am 5. November also der 1. bis 4. November gegen den 1. bis 4. Oktober. Vorjahr = dieselben Kalendertage ein Jahr zuvor, bei der Woche 52 Wochen zurück.",
            "Der Zeitraum endet mit dem letzten aufbereiteten Tag: ab 6 Uhr ist das gestern, davor vorgestern.",
        ],
        use: "Grüne Pfeile heißen besser als der Vergleich, bei Stornos also weniger. Läuft der Buchungswert dem Zahlungseingang davon, stehen Raten aus: dann lohnt der Blick in den Zahlungsverkehr.",
    },
    sources: {
        what: "Über welchen Weg die Buchungen des Zeitraums entstanden sind.",
        how: [
            "Büro: vom Team in der Verwaltung angelegt. Online: vom Kunden selbst im Web-Modul oder in der Kunden-App.",
            "Trägt der Buchungslink eine Quelle (utm_source, zum Beispiel newsletter oder instagram), erscheint sie als eigene Zeile. Online-Buchungen ohne Quelle heißen Direkt.",
            "Die Pfeile zeigen die Veränderung der Buchungszahl je Quelle gegenüber dem Vergleichszeitraum. „neu“ heißt: im Vergleichszeitraum gab es diese Quelle noch nicht.",
            "Anteile unter einem halben Prozent stehen als „< 1 %“.",
        ],
        use: "Hänge an Links in Newsletter, Social Media und auf Flyern ein utm_source an, zum Beispiel ?utm_source=newsletter. Dann siehst du hier, welcher Kanal Buchungen bringt.",
    },
    repeat: {
        what: "Wie viele Buchungen des Zeitraums von Kindern kommen, die bei dir schon einmal in einem Kurs waren. Die Linie zeigt den Anteil je Monat über zwölf Monate, grau das Vorjahr.",
        how: [
            "Zählt Buchungen nach Buchungsdatum, wie die Kachel „Geschäft im Vergleich“. Wartelisteneinträge zählen erst, wenn sie nachrücken.",
            "Wiederbucher: der Teilnehmer hatte am Buchungstag schon eine aktive Buchung für einen anderen Kurs. Ein vorher stornierter erster Versuch zählt nicht als Rückkehr; ein späteres Storno ändert den Tag nicht mehr.",
            "Gezählt wird das Kind, nicht die Familie. Bucht ein Geschwisterkind zum ersten Mal, ist das ein Erstbucher.",
            "Eine Umbuchung zählt nicht als Wiederbuchung: Die bisherige Buchung wird dabei storniert, das Kind bleibt im laufenden Kurs.",
            "Der Anteil vergleicht sich in Prozentpunkten: von 50 auf 55 Prozent sind +5 Punkte.",
        ],
        use: "Ein hoher Anteil heißt, dass Kinder nach dem Kurs bleiben. Fällt er, lohnt der Blick auf das Kursende: Gibt es einen passenden Folgekurs, und wissen die Eltern rechtzeitig davon?",
    },
    funnel: {
        what: "Wie viele Besucher der Kunden-App eine Kursliste sehen, einen Kurs öffnen, die Kasse erreichen und buchen. Die Kennzahl ist der Anteil der Besucher, die am Ende buchen.",
        how: [
            "Gezählt wird am Server an den Seitenaufrufen der Kunden-App, ohne Cookies und ohne Personendaten: ein Besucher ist eine Kombination aus Internetadresse und Browser, die nur als Zählmarke gespeichert wird.",
            "Je Tag zählt jeder Besucher einmal pro Stufe, und jede Stufe schließt die späteren ein: Wer per Direktlink einen Kurs öffnet oder bucht, zählt auch als Besucher. Über den Zeitraum werden die Tage summiert: wer an zwei Tagen vorbeischaut, zählt zweimal.",
            "Suchmaschinen und andere Crawler werden erkannt und nicht gezählt.",
            "„Zur Kasse“ heißt: angemeldet und die Buchungszusammenfassung geöffnet. „Gebucht“ zählt Besucher mit mindestens einer abgeschlossenen Online-Buchung, nicht die Zahl der Buchungen.",
            "Der Trichter kennt keinen Standort und zählt erst seit seiner Freischaltung; ältere Zeiträume bleiben leer. Anteile vergleichen sich in Prozentpunkten.",
        ],
        use: "Bricht der Trichter zwischen Kursliste und Kurs ab, fehlt meist das passende Angebot oder die Liste ist unübersichtlich. Verlieren viele Besucher zwischen Kasse und Buchung, lohnt der Blick auf Preis, Zahlarten und Pflichtfelder im letzten Schritt.",
    },
};

/** Kurzdefinitionen für die Kennzahl-Kästchen, als Tooltip am Label. */
export const statHelp: Record<string, string> = {
    Buchungen: "Neu angelegte Buchungen nach Buchungsdatum. Später stornierte bleiben mitgezählt, Wartelisteneinträge zählen erst beim Nachrücken.",
    Buchungswert: "Summe der Buchungspreise dieser Buchungen, brutto nach Rabatt, unabhängig von der Zahlung.",
    Zahlungseingang: "Raten, die im Zeitraum als bezahlt erfasst wurden, egal wann gebucht wurde.",
    Stornos: "Stornierungen nach Stornodatum, auch von Wartelisteneinträgen.",
    "Neue Kunden": "Im Zeitraum neu angelegte Bucher, online wie im Büro.",
    "Belegung gesamt": "Belegte Plätze geteilt durch alle Plätze der Kurseinheiten im Zeitraum.",
    "Vollste Stunde": "Wochentag und Startstunde mit der höchsten Belegung; nur Stunden mit mindestens zwei Einheiten, solange es genug davon gibt.",
    "Freieste Stunde": "Wochentag und Startstunde mit der niedrigsten Belegung; nur Stunden mit mindestens zwei Einheiten, solange es genug davon gibt.",
    "Online gebucht": "Vom Kunden selbst gebucht, im Web-Modul oder in der Kunden-App.",
    "Vom Büro gebucht": "Vom Team in der Verwaltung angelegt.",
    "Mit bekannter Quelle": "Online-Buchungen, deren Buchungslink ein utm_source trug.",
    Wiederbucher: "Buchungen im Zeitraum, deren Kind vorher schon einen anderen Kurs gebucht hatte.",
    Erstbucher: "Buchungen im Zeitraum, deren Kind zum ersten Mal bei dir bucht.",
};

/** Link in die Anleitung; der utm-Parameter zeigt in der Doku-Statistik, aus welcher Kachel Hilfe gesucht wird. */
export const docsUrl = (anchor?: TileId): string => `${DOCS_BASE_URL}${REPORTS_DOCS_SLUG}?utm_source=app&utm_content=reports-${anchor ?? "page"}${anchor ? `#${anchor}` : ""}`;

export const HelpIcon: FC<{id: TileId; title: string}> = ({id, title}) => {
    const help = tileHelp[id];
    const content = (
        <div className={css.help}>
            <p>{help.what}</p>
            <div className={css.helpHead}>{"So wird gerechnet"}</div>
            <ul>
                {help.how.map((line) => (
                    <li key={line}>{line}</li>
                ))}
            </ul>
            <div className={css.helpHead}>{"Was du daraus machen kannst"}</div>
            <p>{help.use}</p>
            <a href={docsUrl(id)} target="_blank" rel="noopener noreferrer">
                {"Mehr dazu in der Anleitung"}
            </a>
        </div>
    );

    return (
        <Popover content={content} title={title} placement="bottomRight" trigger={["hover", "focus"]} mouseEnterDelay={0.15}>
            <button type="button" className={css.helpButton} aria-label={`Erklärung zu „${title}“`}>
                <InfoCircleOutlined />
            </button>
        </Popover>
    );
};

export const StatLabel: FC<{label: string}> = ({label}) => {
    const help = statHelp[label];

    if (!help) {
        return <span>{label}</span>;
    }

    return (
        <Tooltip title={help} mouseEnterDelay={0.1}>
            <span className={css.statLabelHelp}>{label}</span>
        </Tooltip>
    );
};
